import React from 'react';
import styled from 'styled-components';
import { COLORS, sp } from '../styles';
import { Checkmark, InfoBorder } from './icons';

export type AccentNoticeLevel = 'success' | 'error' | 'warning';

const variants: Record<
    AccentNoticeLevel,
    { bg: string; accent: string; iconColor: string; icon: React.ReactNode }
> = {
    success: {
        bg: COLORS.accentNoticeSuccessBg,
        accent: COLORS.accentNoticeSuccessAccent,
        iconColor: COLORS.accentNoticeSuccessAccent,
        icon: <Checkmark />,
    },
    error: {
        bg: COLORS.accentNoticeErrorBg,
        accent: COLORS.accentNoticeErrorAccent,
        iconColor: COLORS.accentNoticeErrorAccent,
        icon: <InfoBorder />,
    },
    // The only level whose icon does not take the accent color: the yellow
    // accent is unreadable at 16px, so the glyph keeps the body text color.
    warning: {
        bg: COLORS.accentNoticeWarningBg,
        accent: COLORS.accentNoticeWarningAccent,
        iconColor: COLORS.accentNoticeFg,
        icon: <InfoBorder />,
    },
};

const Wrapper = styled.div<{ $bg: string }>`
    display: flex;
    inline-size: 100%;
    max-inline-size: 100%;
    gap: ${sp(2.25)};
    border: 1px solid ${COLORS.accentNoticeBorder};
    border-radius: 6px;
    overflow: hidden;
    color: ${COLORS.accentNoticeFg};
    background-color: ${({ $bg }) => $bg};
`;

const Accent = styled.span<{ $accent: string }>`
    flex-shrink: 0;
    inline-size: 6px;
    align-self: stretch;
    background-color: ${({ $accent }) => $accent};
`;

const Body = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    gap: ${sp(1.5)};
    padding: ${sp(1.5)};
    padding-inline-start: 0;
    min-inline-size: 0;
`;

const IconSlot = styled.span<{ $accent: string }>`
    display: inline-flex;
    flex-shrink: 0;
    inline-size: 16px;
    block-size: 16px;
    color: ${({ $accent }) => $accent};
`;

const Content = styled.div`
    flex: 1;
    min-inline-size: 0;
    display: flex;
    flex-direction: column;
    gap: ${sp(0.5)};
`;

const Title = styled.div`
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
`;

const Description = styled.div`
    font-size: 14px;
    line-height: 1.4;
`;

interface AccentNoticeProps {
    level: AccentNoticeLevel;
    title?: string | null;
    description?: string | null;
    dataAuto?: string;
}

const AccentNotice: React.FC<AccentNoticeProps> = ({
    level,
    title,
    description,
    dataAuto,
}) => {
    const variant = variants[level];

    return (
        <Wrapper $bg={variant.bg} data-auto={dataAuto}>
            <Accent $accent={variant.accent} aria-hidden='true' />
            <Body>
                <IconSlot $accent={variant.iconColor} aria-hidden='true'>
                    {variant.icon}
                </IconSlot>
                <Content>
                    {title && <Title>{title}</Title>}
                    {description && <Description>{description}</Description>}
                </Content>
            </Body>
        </Wrapper>
    );
};

export default AccentNotice;
